import React from 'react';

import moment from 'moment';

import { Alert, AlertTitle } from '@material-ui/lab';

import { UiDialog } from '../../ui/ui-dialog';

import { SourceStatus } from './applicant-source-status';
import { ApplicantSourceStatusAccordion } from './applicant-source-status-accordion';

import { SOURCE_TYPES } from '../../util/application-source-types';

type InputProps = {
  onClose: () => void;
  status: SourceStatus | null;
  sourceIssues: ApplicationSourceIssue[];
  job: Job;
  org: Org | null;
};

const individualStatus = (status: SourceStatus | null, issues: Array<string>) => {
  const { NEEDS_REVIEW, PENDING, SUCCESS } = SourceStatus;
  if (!status) {
    return null;
  }
  if (status === PENDING) {
    return PENDING;
  }
  if (issues.length) {
    return NEEDS_REVIEW;
  }
  return SUCCESS;
};

const JobCreationMessage = ({ pendingDuration }: { pendingDuration: string }) => (
  <>
    <AlertTitle>This job is posting to our applicant sources</AlertTitle>
    It could take up to {pendingDuration} for the job to be active on all sources. The pending icon
    will be removed after that time.
  </>
);

const JobUpdateMessage = ({ pendingDuration }: { pendingDuration: string }) => (
  <>
    <AlertTitle>Your updates to this job are publishing to our applicant sources</AlertTitle>
    The old version of your job is active, and will remain active while it updates. It could take up
    to {pendingDuration} for your updates to publish to all sources. The pending icon will be
    removed after that time.
  </>
);

export const ApplicantSourceStatusDialog = ({
  status,
  sourceIssues,
  job,
  org,
  onClose,
}: InputProps) => {
  const { PENDING, NA } = SourceStatus;

  const mostRecentAction = moment.max([moment(job.lastActivatedAt), moment(job.lastUpdatedAt)]);
  const pendingDuration = mostRecentAction
    ? moment(mostRecentAction).add(12.5, 'hours').startOf('hour').fromNow(true)
    : '12 hours';
  const wasJustUpdated = moment(job.lastUpdatedAt)
    .add(12.5, 'hours')
    .startOf('hour')
    .isAfter(moment());

  const daysAgo = mostRecentAction ? moment().dayOfYear() - mostRecentAction.dayOfYear() : 0;
  const humanizedDaysAgo =
    daysAgo === 0
      ? 'today'
      : daysAgo === 1
      ? 'yesterday'
      : `on ${mostRecentAction.format('dddd, MMMM Do, YYYY')}`;

  const indeedIssues = sourceIssues
    .filter((issue) => issue.sourceType === SOURCE_TYPES.INDEED)
    .map((issue) => issue.applicationSourceIssueType);
  const indeedStatus = individualStatus(status, indeedIssues);
  const unknownStatus = status === PENDING ? status : NA;

  return (
    <UiDialog title="Applicant Source Status" onClose={onClose}>
      <UiDialog.Content>
        {status === PENDING && (
          <Alert className="u-margin-bottom" severity="info">
            {wasJustUpdated ? (
              <JobUpdateMessage pendingDuration={pendingDuration} />
            ) : (
              <JobCreationMessage pendingDuration={pendingDuration} />
            )}
            <br />
            {mostRecentAction ? (
              <>
                Last edited {humanizedDaysAgo} at {mostRecentAction.format('hh:mm A')}.
              </>
            ) : null}
          </Alert>
        )}
        <ApplicantSourceStatusAccordion
          status={indeedStatus}
          issueTypes={indeedIssues}
          sourceName="Indeed"
          job={job}
          org={org}
        />
        <ApplicantSourceStatusAccordion status={unknownStatus} sourceName="ZipRecruiter" />
        <ApplicantSourceStatusAccordion status={unknownStatus} sourceName="Google Jobs" />
        <ApplicantSourceStatusAccordion sourceName="Other Sources">
          <div className="u-typo-body">
            We also post to other sources including JobisJob and Jooble but they do not provide
            feedback on the status of your post. See{' '}
            <a
              href="https://help.teamengine.io/article/48-applicant-sourcing"
              className="typo-link"
              target="_blank"
              rel="noreferrer noopener"
            >
              Applicant Sourcing
            </a>
          </div>
        </ApplicantSourceStatusAccordion>
      </UiDialog.Content>
    </UiDialog>
  );
};
